import { sortBy } from 'lodash';
import { type ReactElement } from 'react';

import { isTruthy } from 'common';

export function truncate(name: string, maxLength: number): string {
  return name.length > maxLength ? name.slice(0, maxLength) + '...' : name;
}

export function highlightMatches(haystack: string, needle: string): ReactElement | null {
  const lowerHaystack = haystack.toLocaleLowerCase();
  const lowerNeedle = needle.toLocaleLowerCase();

  if (!lowerNeedle.length) {
    return <span>{haystack}</span>;
  }

  let start = 0;
  let foundAny = false;
  const textParts: ReactElement[] = [];
  while (start < lowerHaystack.length) {
    const foundIndex = lowerHaystack.indexOf(lowerNeedle, start);
    if (foundIndex < 0) {
      break;
    }
    foundAny = true;

    const unMatchedPart = haystack.substring(start, foundIndex);
    const matchedPart = haystack.substr(foundIndex, needle.length);

    if (unMatchedPart) {
      textParts.push(<span key={textParts.length}>{unMatchedPart}</span>);
    }
    if (matchedPart) {
      textParts.push(<b key={textParts.length}>{matchedPart}</b>);
    }
    start = foundIndex + lowerNeedle.length;
  }

  if (start !== lowerHaystack.length) {
    const unMatchedPart = haystack.substring(start);
    textParts.push(<span key={textParts.length}>{unMatchedPart}</span>);
  }

  if (!foundAny) {
    return null;
  }

  return <>{textParts}</>;
}

// Duplicated in backend/primary/src/utils/phone.ts — keep in sync
export const SHORT_CODE_REGEX = /^\d{5,6}$/;

export function isSipUriTriggerKey(value: string | undefined | null): boolean {
  return isTruthy(value) && value.toLowerCase().startsWith('sip:');
}

export function formatPhoneNumber(phoneNumber: string) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  if (SHORT_CODE_REGEX.test(cleaned)) {
    return cleaned;
  }
  const match = /^(1|)?(\d{3})(\d{3})(\d{4})$/.exec(cleaned);
  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
}

export function formatPercent(
  numerator: number,
  denominator: number,
  fractionDigits?: number
): string {
  if (denominator === 0) {
    return 'N/A';
  }

  const rawPercent = (numerator / denominator) * 100;
  if (fractionDigits) {
    return `${rawPercent.toFixed(fractionDigits)}%`;
  }

  let percent = Math.round(rawPercent);
  if (percent === 100 && numerator < denominator) {
    percent = 99;
  } else if (percent === 0 && numerator > 0) {
    percent = 1;
  }
  return `${percent}%`;
}

export function formatCurrencyInUsd(amountInUsCents: number): string {
  return (amountInUsCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'usd',
  });
}

export function sortIgnoreCase<T extends string>(values: readonly T[]): T[] {
  return sortBy(values, (value) => value.toLocaleLowerCase());
}

const REGEX_INTERPOLATE = /{{([\s\S]+?)}}/g;
export enum TemplateInterpolationFallback {
  Empty = 'Empty',
  Template = 'Template',
}
export function interpolateTemplateValues(
  template: string,
  values: Record<string, string>,
  interpolationFallback = TemplateInterpolationFallback.Empty
): string {
  return template.replaceAll(
    REGEX_INTERPOLATE,
    (_, key: string) =>
      values[key.trim()] ||
      (interpolationFallback === TemplateInterpolationFallback.Template ? `{{${key}}}` : '')
  );
}

/**
 * Converts a space-separated string to SCREAMING_SNAKE_CASE format.
 * Example: "Pacific Islander" -> "PACIFIC_ISLANDER"
 * Example: "TWLC Revive" -> "TWLC_REVIVE"
 */
export function toScreamingSnakeCase(input: string): string {
  const trimmedInput = input.trim();
  const separatedWords: string[] = trimmedInput.split(' ');
  const upperCasedWords = separatedWords.map((word) => word.toUpperCase());
  return upperCasedWords.join('_');
}
